import { useCallback, type CSSProperties } from "react";

import { Trash2Icon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { cx, styled } from "simplydo/react/styled";
import type { UiColor } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { ColorChooser } from "simplydo/react/ui/color-chooser";
import { Icon } from "simplydo/react/ui/icon";

import {
  ROLE_ICONS,
  ROLE_ICON_NAMES,
  isRoleIconName,
  type RoleIconName,
} from "../roleIcons";
import type { RoleSettingsUpdateBody } from "../types";

const IconChooserButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 5px;
`;

const IconChooserButton = styled.div`
  cursor: pointer;
  border-radius: 5px;
  padding: 7.5px;

  &:hover:not(.is-selected) {
    background-color: #f6f6f6;
  }

  &.is-selected {
    background-color: #dfdfdf;
  }

  > svg.icon {
    margin: 0;
  }
`;

type RoleEditorIconChooserProps = {
  containerStyle?: CSSProperties;
  role: OpenAPI.Schemas["Role"];
  updateRole: (data: RoleSettingsUpdateBody) => void;
};

const RoleEditorIconChooser = ({
  containerStyle = {},
  role,
  updateRole,
}: RoleEditorIconChooserProps) => {
  const { icon = { name: "", colour: "" } } = role ?? {};
  const { name: selectedIcon, colour: selectedColor } = icon;

  const setSelectedIcon = useCallback(
    (props: { name?: RoleIconName; colour?: UiColor }) => {
      if (props.name && !isRoleIconName(props.name)) return;
      updateRole({
        icon: { ...(role.icon ?? {}), name: props.name, colour: props.colour },
      });
    },
    [role, updateRole],
  );

  const removeIcon = useCallback(() => {
    updateRole({ icon: null });
  }, [updateRole]);

  return (
    <div style={containerStyle}>
      <IconChooserButtonGroup>
        {ROLE_ICON_NAMES.map((iconName) => (
          <div
            key={iconName}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ColorChooser
              trigger={
                <IconChooserButton
                  className={cx(selectedIcon === iconName && "is-selected")}
                >
                  <Icon
                    icon={ROLE_ICONS[iconName]}
                    size="large"
                    color={
                      iconName === selectedIcon ? selectedColor : undefined
                    }
                  />
                </IconChooserButton>
              }
              fluid
              value={iconName === selectedIcon ? selectedColor : undefined}
              onChange={(color) => {
                setSelectedIcon({ name: iconName, colour: color });
              }}
            />

            {selectedIcon == iconName ? (
              <Button
                size="small"
                onClick={() => removeIcon()}
                style={{ marginTop: 10 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
                Remove icon
              </Button>
            ) : null}
          </div>
        ))}
      </IconChooserButtonGroup>
    </div>
  );
};

export default RoleEditorIconChooser;
